import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { farmvestService } from '../../../services/farmvest_api';
import { formatApiError } from '../../../services/apiUtils';

export const fetchInvestors = createAsyncThunk(
    'farmvestInvestors/fetchInvestors',
    async (params: { page?: number; size?: number; is_active?: boolean; search?: string; cpf_pending?: boolean; is_onboarded?: boolean } | undefined, { rejectWithValue }) => {
        try {
            const response = await farmvestService.getAllInvestors(params);

            let rawData: any[] = [];
            let totalCount = 0;

            // Handle various response structures (direct array, data.investors, data.users, etc.)
            if (Array.isArray(response)) {
                rawData = response;
            } else if (response && Array.isArray(response.data)) {
                rawData = response.data;
            } else if (response && response.data) {
                rawData = response.data.investors || response.data.users || response.data.data || (Array.isArray(response.data) ? response.data : []);
            } else if (response && (response.users || response.investors)) {
                rawData = response.users || response.investors;
            }

            // Extract totalCount from various possible locations
            const pagination = response?.pagination || response?.data?.pagination;
            totalCount =
                pagination?.total_items ||
                pagination?.total_count ||
                response?.total_users ||
                response?.total_investors ||
                response?.total ||
                response?.count ||
                response?.total_count ||
                rawData.length;

            const mappedData = rawData.map((item: any, index: number) => {
                // Determine active status robustly (1 or 0)
                const rawStatus = item.active_status !== undefined ? item.active_status : (item.is_active !== undefined ? item.is_active : true);
                const isActive = typeof rawStatus === 'boolean' ? (rawStatus ? 1 : 0) : Number(rawStatus);

                return {
                    ...item,
                    id: item.id || item.investor_id || item.user_id || index,
                    investor_id: item.investor_id || item.id || item.user_id,
                    first_name: item.first_name || item.name?.split(' ')[0] || '',
                    last_name: item.last_name || item.name?.split(' ').slice(1).join(' ') || '',
                    email: item.email || '',
                    mobile: item.mobile || item.phone_number || '',
                    phone_number: item.phone_number || item.mobile || '',
                    active_status: isActive,
                    is_active: isActive,
                    created_at: item.created_at || item.joining_date || '',
                    address: item.address || [item.location, item.city, item.street_address, item.landmark].filter(Boolean).join(', ') || '-',
                    role_status: item.role_status || (() => {
                        if (Array.isArray(item.roles_detail) && item.roles_detail.some((rd: any) => rd.is_active !== undefined)) {
                            return Object.fromEntries(item.roles_detail.map((rd: any) => [String(rd.role).toUpperCase(), !!rd.is_active]));
                        }
                        return undefined;
                    })(),
                };
            });

            const statusCounts = {
                active: Number(response?.active_count ?? response?.data?.active_count ?? 0),
                inactive: Number(response?.inactive_count ?? response?.data?.inactive_count ?? 0),
            };
            const totalBuffaloes = Number(response?.total_buffalo_count ?? response?.data?.total_buffalo_count ?? 0);
            const totalUnits = Number(response?.total_units_count ?? response?.data?.total_units_count ?? response?.total_units ?? response?.data?.total_units ?? 0);

            return { investors: mappedData, totalCount, statusCounts, totalBuffaloes, totalUnits };
        } catch (error: any) {
            return rejectWithValue(formatApiError(error.response?.data?.detail || error.message || 'Failed to fetch investors'));
        }
    }
);

interface InvestorsState {
    investors: any[];
    totalCount: number;
    totalBuffaloes: number;
    totalUnits: number;
    loading: boolean;
    error: string | null;
    statusCounts: { active: number; inactive: number };
    globalTotalCount: number;
    globalStatusCounts: { active: number; inactive: number };
    globalTotalBuffaloes: number;
    globalTotalUnits: number;
}

const initialState: InvestorsState = {
    investors: [],
    totalCount: 0,
    totalBuffaloes: 0,
    totalUnits: 0,
    loading: false,
    error: null,
    statusCounts: { active: 0, inactive: 0 },
    globalTotalCount: 0,
    globalStatusCounts: { active: 0, inactive: 0 },
    globalTotalBuffaloes: 0,
    globalTotalUnits: 0,
};

const investorsSlice = createSlice({
    name: 'farmvestInvestors',
    initialState,
    reducers: {
        clearInvestorErrors: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInvestors.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchInvestors.fulfilled, (state, action) => {
                state.loading = false;
                state.investors = action.payload.investors;
                state.totalCount = action.payload.totalCount;

                // Track global stats separately for the dashboard cards
                const isGlobalRequest = !action.meta.arg?.search && action.meta.arg?.is_active === undefined;

                if (isGlobalRequest || (state.globalTotalCount === 0)) {
                    if (action.payload.totalCount > 0) {
                        state.globalTotalCount = action.payload.totalCount;
                    }
                    if (action.payload.statusCounts.active > 0 || action.payload.statusCounts.inactive > 0) {
                        state.globalStatusCounts = action.payload.statusCounts;
                    }
                    if (action.payload.totalBuffaloes > 0) {
                        state.globalTotalBuffaloes = action.payload.totalBuffaloes;
                    }
                    if (action.payload.totalUnits > 0) {
                        state.globalTotalUnits = action.payload.totalUnits;
                    }
                }

                // Also update the current view's counts for legacy support/internal use
                state.statusCounts = action.payload.statusCounts;
                state.totalBuffaloes = action.payload.totalBuffaloes;
                state.totalUnits = action.payload.totalUnits;
            })
            .addCase(fetchInvestors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearInvestorErrors } = investorsSlice.actions;
export const investorsReducer = investorsSlice.reducer;
export default investorsReducer;
